import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logo from '../../images/LogohvidFinal.png';
import {
    WORKBOOK_STEPS,
    type WorkbookStepKey,
} from './workbookWizardConstants';
import LogoutButton from '../general/LogoutButton';

interface WorkbookProgressTrackerProps {
    currentStep: WorkbookStepKey;
}

export default function WorkbookProgressTracker({
    currentStep,
}: WorkbookProgressTrackerProps) {
    const { t } = useTranslation();
    const currentIndex = WORKBOOK_STEPS.findIndex(
        (step) => step.key === currentStep
    );

    return (
        <div className="relative flex w-full flex-col justify-between overflow-hidden border-b border-subtle bg-muted lg:w-80 lg:border-b-0 lg:border-r">
            <div className="relative z-10 p-6 lg:p-8">
                <div className="mb-10">
                    <img src={logo} alt="NordInsight" className="h-9" />
                </div>

                <p className="mb-4 px-3 text-sm font-medium uppercase text-foreground/50">
                    {t('onboarding.setupProgress')}
                </p>

                <div>
                    {WORKBOOK_STEPS.map((step, index) => {
                        const isCompleted = index < currentIndex;
                        const isActive = index === currentIndex;
                        const Icon = step.icon;

                        return (
                            <div key={step.key}>
                                <div
                                    className={`flex items-start gap-3.5 rounded-lg px-3 py-3 transition-colors ${
                                        isActive ? 'bg-muted' : ''
                                    }`}
                                >
                                    <span
                                        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium transition-all ${
                                            isCompleted
                                                ? 'bg-emerald-500 text-background'
                                                : isActive
                                                ? 'border-2 border-emerald-400 bg-emerald-500/10 text-emerald-400'
                                                : 'bg-accent text-muted-foreground/60'
                                        }`}
                                    >
                                        {isCompleted ? (
                                            <Check
                                                className="h-4 w-4"
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            <Icon
                                                className="h-3.5 w-3.5"
                                                aria-hidden="true"
                                            />
                                        )}
                                    </span>
                                    <div>
                                        <span
                                            className={`text-sm font-medium ${
                                                isActive || isCompleted
                                                    ? 'text-foreground'
                                                    : 'text-muted-foreground/60'
                                            }`}
                                        >
                                            {t(`onboarding.steps.${step.key}.label`)}
                                        </span>
                                        <p
                                            className={`mt-0.5 text-xs ${
                                                isActive
                                                    ? 'text-muted-foreground'
                                                    : 'text-muted-foreground/40'
                                            }`}
                                        >
                                            {t(`onboarding.steps.${step.key}.description`)}
                                        </p>
                                    </div>
                                </div>

                                {index < WORKBOOK_STEPS.length - 1 && (

                                    <div className="ml-[22px] h-4 w-0.5 rounded-full bg-accent">
                                        <div
                                            className="w-full rounded-full bg-emerald-500 transition-all duration-500"
                                            style={{
                                                height: isCompleted
                                                    ? '100%'
                                                    : '0%',
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="relative z-10 border-t border-subtle p-6 lg:p-8">
                <LogoutButton className="w-full border-subtle bg-muted text-xs text-foreground/80 hover:bg-card hover:text-foreground" />
            </div>
        </div>
    );
}
